import {type Line} from '../../../util/plotHelpers/types';

/**
 * Represents a series entry for uPlot data alignment.
 * - 'primary-series': A regular line series, including Show Original overlays
 * - 'band-min': The min (y) values from an aux area line rendered as a uPlot band
 * - 'band-max': The max (y0) values from an aux area line rendered as a uPlot band
 */
export type SeriesEntry = {
  line: Line;
  type: 'primary-series' | 'band-min' | 'band-max';
};

type SeriesIndices = {
  bandMinSeriesIndex?: number;
  bandMaxSeriesIndex?: number;
  primarySeriesIndex?: number;
};

type LineMetricMap = Map<
  string, // metric name
  SeriesIndices
>;

export type LineNameMap = Map<
  string, // line name
  LineMetricMap
>;

type LineWithSeriesIdentity = Line & {
  metricName: string;
  name: string;
};

function isUPlotBandSourceLine(line: Line): boolean {
  return line.aux === true && line.meta.type === 'area';
}

function hasSeriesIdentity(line: Line): line is LineWithSeriesIdentity {
  return line.name != null && line.metricName != null;
}

function isCanonicalPrimarySeriesLine(
  line: Line
): line is LineWithSeriesIdentity {
  return line.aux !== true && hasSeriesIdentity(line);
}

// Build band entries first (so they render behind line series)
export function getSeriesEntries(lines: Line[]): {
  seriesEntries: (SeriesEntry | null)[];
  lineNameMap: LineNameMap;
} {
  // null entry is to align the array with uPlot's 1-indexed series
  const entries: (SeriesEntry | null)[] = [null];

  // `primary-series` is a render shape, not a canonical identity role.
  // Show Original overlays are aux line-shaped data: they must still render as
  // primary-series entries, but they must not populate primarySeriesIndex.
  // Band source lines are aux area-shaped data: only these become band-min/max.
  // Do not collapse these decisions back to `line.aux`; aux is legacy
  // supporting-series metadata, not a rendering discriminator.
  const primarySeriesLines: Line[] = [];
  const bandSourceLines: Line[] = [];
  for (const line of lines) {
    if (isUPlotBandSourceLine(line)) {
      bandSourceLines.push(line);
    } else {
      primarySeriesLines.push(line);
    }
  }

  const lineNameMap = new Map<string, LineMetricMap>();
  for (const line of bandSourceLines) {
    if (line.name != null && line.metricName != null) {
      let lineMetricMap = lineNameMap.get(line.name);
      if (!lineMetricMap) {
        lineMetricMap = new Map<string, SeriesIndices>();
      }
      lineMetricMap.set(line.metricName, {
        bandMinSeriesIndex: entries.length,
        bandMaxSeriesIndex: entries.length + 1,
      });
      lineNameMap.set(line.name, lineMetricMap);
    }
    entries.push({type: 'band-min', line});
    entries.push({
      type: 'band-max',
      line: line,
    });
  }

  // Then add primary-series lines on top. Aux line overlays render as
  // primary-series entries, but only canonical primary lines populate
  // primarySeriesIndex for run/metric identity lookups.
  for (const line of primarySeriesLines) {
    if (isCanonicalPrimarySeriesLine(line)) {
      let lineMetricMap = lineNameMap.get(line.name);
      if (!lineMetricMap) {
        lineMetricMap = new Map<string, SeriesIndices>();
      }
      const lineNameMapEntry = lineMetricMap.get(line.metricName);

      lineMetricMap.set(line.metricName, {
        ...lineNameMapEntry,
        primarySeriesIndex: entries.length,
      });
      lineNameMap.set(line.name, lineMetricMap);
    }
    entries.push({type: 'primary-series', line});
  }

  return {seriesEntries: entries, lineNameMap};
}
